import { FC } from 'react';
import { convertToFormattedDate, convertToIsoDate, uploadFile } from 'src/utils';

import { Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import EditIcon from '@mui/icons-material/Edit';

import { useAuth } from 'src/contexts';
import { WordBreak } from 'src/components';
import { Panel, Divider, IconButton, ItemCard, FieldRow, ItemRow, FieldLabel, Title, RadioLabel, FieldNote } from 'src/generic/component';
import { FileDetails, FileUploader } from 'src/generic/component/file';

import { IMaterial } from 'src/types';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { Collection, FileCollection, useSubform } from 'src/generic/type';

import { Draft } from 'src/types/common';
import { IDistance } from 'src/types/org/common/distanceSchema';
import { INameTitle } from 'src/types/org/common/nameTitleSchema';
import { IRoom } from 'src/types/org/common/roomSchema';
import { ITestingPlan } from 'src/types/org/common/testingPlanSchema';

import { NameTitleForm, NameTitleFields } from './nameTitleForm';
import { NameTitleDetails } from './nameTitleDetails';
import { RoomForm } from './roomForm';
import { DistanceFields } from './distanceForm';

import { IntegerInput, BooleanRadio, Input, TextArea } from 'src/generic/component/form';


export interface ITestingPlanFieldProps {
  categoryId: string;
  userId: string;
  readOnly?: boolean;
  item: Draft<ITestingPlan>;
  validating?: boolean;
  errors: IFieldErrors;
  original?: Draft<ITestingPlan>;
  showDiff?: boolean;
  onUpdate?: (data: Draft<ITestingPlan>) => void;
}

export const TestingPlanFields: FC<ITestingPlanFieldProps> = ({ userId, categoryId, item: initial, readOnly, errors, onUpdate }) => {
  const { userName } = useAuth();

  /**
   * Form Variables and Monitoring Funcationality
   * 
   * This is the place where most additions and modifications will occur for the form
   * in addition to the rendering block.
   */

  const testingRoomPictures: IMaterial[] = initial.testingRoomPictures || [];
  const testTrainers: INameTitle[] = initial.testAdministrationTrainingIndividuals || [];
  const outsideFacilityPictures: IMaterial[] = initial.outsideFacilityPictures || [];
  const secureMaterialsManagerIndividualDetails: Partial<INameTitle> = initial.secureMaterialsManagerIndividualDetails || {};
  const secureMaterialsAccessIndividualDetails: INameTitle[] = initial.secureMaterialsAccessIndividualDetails || [];
  const storageAreaPictures: IMaterial[] = initial.storageAreaPictures || [];
  const testSupervisorDetail: Partial<INameTitle> = initial.testSupervisorDetail || {};
  const distanceBetweenTestingRoomAndStorageArea: Partial<IDistance> = initial.distanceBetweenTestingRoomAndStorageArea || {};
  const testingRoomExamMaterialDistributionIndividuals: INameTitle[] = initial.testingRoomExamMaterialDistributionIndividuals || [];
  const accomodationTestingCoordinatorDetails: Partial<INameTitle> = initial.accomodationTestingCoordinatorDetails || {};
  const testingMaterialsAccountingIndividualDetails: Partial<INameTitle> = initial.testingMaterialsAccountingIndividualDetails || {};
  const testingRoomDetails: IRoom[] = initial.testingRoomDetails || [];

  /**
   * Room Form Management
   */
  const roomEntities = Collection<IRoom>(testingRoomDetails);
  const roomForm = useSubform<IRoom>();

  /**
   * Trainer Form Management
   */
  const trainerEntities = Collection<INameTitle>(testTrainers);
  const trainerForm = useSubform<INameTitle>();

  /**
   * Access Form Management
   */
  const accessEntities = Collection<INameTitle>(secureMaterialsAccessIndividualDetails);
  const accessForm = useSubform<INameTitle>();

  /**
   * Distribution Individuals Management
   */
  const distributionEntities = Collection<INameTitle>(testingRoomExamMaterialDistributionIndividuals);
  const distributionForm = useSubform<INameTitle>();

  /**
   * Testing Room Pictures
   */
  const roomPictureCollection = FileCollection(testingRoomPictures);
  const outsidePictureCollection = FileCollection(outsideFacilityPictures);
  const storagePictureEntities = FileCollection(storageAreaPictures);


  const {
    facilityOwnedByOrg,
    organizationLocatedDuration,
    hasOrgRelocationPlanned,
    organizationRelocationPlannedDate,
    testingTableTypes,
    hasSeatingSameDirection,
    hasProperSpacing,
    hasRandomSeating,
    assessmentTrainingConfirmation,
    testAlertConfirmation,
    testingRoomStaffMembersCount,
    nonTestingRoomStaffMembersCount,
    plannedTestDayBreakSchedule,
    howToStoreMaterials,
    secureMaterialsStorageKeysCount,
    testingMaterialsPreparationDescription,
    testingRoomsTransportation,
    testSupervisorConfirmation,
    orgTestPolicyConfirmation,
    outsideStudentsVerification,
    listAcceptedIDForms,
    lateArrivalsHandleDetails,
    facilityHandleTestTakersDetails,
    accomodationSeparateTestingRoomsConfirmation,
    extendedTimeFacilityRemainConfirmation,
    secureMaterialsReturnLocation,
    trainingProvisionDetails
  } = initial;

  const changed = (update: Draft<ITestingPlan>) => {
    if (onUpdate)
      onUpdate({ ...initial, ...update });
  };

  const { getError, getFieldError, hasError } = ErrorManager(errors);

  const render = () => {
    return <>
      <div></div>
      <Panel>
        <Title>Facility</Title>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <FieldRow>
              <RadioLabel variant="body1">Does your organization own or lease your facility?</RadioLabel>
              <BooleanRadio 
              readOnly={readOnly} 
              value={facilityOwnedByOrg} 
              name='facilityOwnedByOrg' 
              error={(getError('facilityOwnedByOrg'))} 
              onChange={(val) => { changed({ facilityOwnedByOrg: val }); }} 
               labels={{ trueLabel: 'Own', falseLabel: 'Lease' }}
              />
            </FieldRow>
            <FieldRow>
              <FieldLabel variant="body1">How long has your organization been located at this location?</FieldLabel>
              <Input disabled={readOnly} validation={hasError('organizationLocatedDuration') ? 'error' : undefined} validationMsg={getError('organizationLocatedDuration')}
                onChange={(e) => {
                  changed({ organizationLocatedDuration: e.target.value });
                }}
                value={organizationLocatedDuration}
              />
            </FieldRow>
            <FieldRow>
              <RadioLabel variant="body1">Is there a known date planned for your organization to relocate?</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={hasOrgRelocationPlanned} name='hasOrgRelocationPlanned' error={(getError('hasOrgRelocationPlanned'))} onChange={(val) => { changed({ hasOrgRelocationPlanned: val }); }} />
            </FieldRow>


            {hasOrgRelocationPlanned &&
              <FieldRow>
                {readOnly ?
                  <FieldLabel>Planned Relocation Date: {convertToFormattedDate(organizationRelocationPlannedDate)}</FieldLabel> :
                  <>
                    <FieldLabel variant="body1">Planned Relocation Date</FieldLabel>
                    <Input type="date" value={convertToIsoDate(organizationRelocationPlannedDate)}
                      validation={getError('organizationRelocationPlannedDate') ? 'error' : undefined}
                      validationMsg={getError('organizationRelocationPlannedDate')}
                      onChange={(e) => { changed({ organizationRelocationPlannedDate: e.target.value }); }} />
                  </>
                }
              </FieldRow>
            }
          </Grid>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Outside Facility Pictures</FieldLabel>
              {hasError('outsideFacilityPictures') &&
                <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>{getError('outsideFacilityPictures')}</span>
              }
            </FieldRow>
            <FieldRow>
              {outsideFacilityPictures.map((item, i) => {
                return <FieldRow key={`facility-pictures-${i}`}><FileDetails readOnly={readOnly} data={item} onRemove={(item) => {
                  outsidePictureCollection.remove(item);
                  changed({ outsideFacilityPictures: outsidePictureCollection.items });
                }} /></FieldRow>;
              })}
            </FieldRow>
            {!readOnly &&
              <>
                <FieldRow>
                  <FileUploader
                    uploadButton='Add Picture'
                    onUploadFile={async (file: File, description: string | undefined) => {
                      const fieldMaterial: IMaterial = await uploadFile(userName, { categoryId: categoryId }, file, description, 'outsideFacilityPictures', true);
                      outsidePictureCollection.add(fieldMaterial);
                      changed({ outsideFacilityPictures: outsidePictureCollection.items });
                    }}
                  />
                </FieldRow>
              </>
            }
          </Grid>
        </Grid>
      </Panel>

      <Panel>
        <Title>Testing Plan</Title>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel style={{ marginBottom: '0px' }} variant="body1">Please enter all testing rooms at your facility </FieldLabel>
              <div style={{ lineHeight: 1 }}><FieldNote>(Please note: testing rooms must have sufficient power and WIFI access)</FieldNote></div>
              {hasError('testingRoomDetails') &&
                <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>{getError('testingRoomDetails')}</span>
              }
            </FieldRow>
            <FieldRow>
              {testingRoomDetails.map((item, i) => {
                const isEditing = (roomForm.data === item);
                return <ItemRow key={`test-room-${i}`}>
                  <ItemCard>
                    {!readOnly &&
                      <div style={{ marginRight: '8px' }}>
                        {isEditing ?
                          <div>&nbsp;</div> :
                          <IconButton onClick={() => { roomEntities.remove(item); changed({ testingRoomDetails: roomEntities.items }); }}>
                            <RemoveIcon />
                          </IconButton>
                        }
                        <IconButton style={{ color: isEditing ? '#fa921b' : undefined }} onClick={() => { roomForm.open(item); }} >
                          <EditIcon />
                        </IconButton>
                      </div>
                    }
                    <div>
                      <WordBreak>
                        <WordBreak><span style={{ fontWeight: 500 }}>Description:</span> {item.roomType}</WordBreak>
                        <WordBreak><span style={{ fontWeight: 500 }}>Capacity:</span> {item.maximumCapacity}</WordBreak>
                      </WordBreak>
                    </div>
                  </ItemCard>
                </ItemRow>;
              })}
            </FieldRow>
            <FieldRow>
              {roomForm.isOpen ?
                <RoomForm item={roomForm.data}
                  onCancel={() => { roomForm.close(); }}
                  onComplete={(data) => {
                    const pos = roomForm.data ? roomEntities.match(roomForm.data) : undefined;
                    roomEntities.add(data, pos);
                    roomForm.close();
                    changed({ testingRoomDetails: roomEntities.items });
                  }}
                /> :
                <>
                  {!readOnly &&
                    <IconButton onClick={() => { roomForm.open(); }}><AddIcon />&nbsp;Add Room</IconButton>
                  }
                </>
              }
            </FieldRow>

            <Divider />

            <FieldRow>
              <FieldLabel variant="body1">Upload pictures and descriptions for your testing rooms</FieldLabel>
              {hasError('testingRoomPictures') &&
                <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>{getError('testingRoomPictures')}</span>
              }
            </FieldRow>
            <FieldRow>
              {testingRoomPictures.map((item, i) => {
                return <FieldRow key={`room-pictures-${i}`}><FileDetails readOnly={readOnly} data={item} onRemove={(item) => {
                  roomPictureCollection.remove(item);
                  changed({ testingRoomPictures: roomPictureCollection.items });
                }} /></FieldRow>;
              })}
            </FieldRow>
            {!readOnly &&
              <>
                <FieldRow>
                  <FileUploader
                    uploadButton='Add Picture'
                    onUploadFile={async (file: File, description: string | undefined) => {
                      const fieldMaterial: IMaterial = await uploadFile(userName, { categoryId: categoryId }, file, description, 'testingRoomPictures', true);
                      roomPictureCollection.add(fieldMaterial);
                      changed({ testingRoomPictures: roomPictureCollection.items });
                    }}
                  />
                </FieldRow>
              </>
            }
          </Grid>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">What type of tables will be used for testing?</FieldLabel>
              <Input disabled={readOnly} validation={hasError('testingTableTypes') ? 'error' : undefined} validationMsg={getError('testingTableTypes')}
                onChange={(e) => { changed({ testingTableTypes: e.target.value }); }}
                value={testingTableTypes}
              />
            </FieldRow>
            <FieldRow>
              <RadioLabel variant="body1">Please confirm students will be seated facing the same direction.</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={hasSeatingSameDirection} name='hasSeatingSameDirection' error={(getError('hasSeatingSameDirection'))} onChange={(val) => { changed({ hasSeatingSameDirection: val }); }} />
            </FieldRow>
            <FieldRow>
              <RadioLabel variant="body1">Please confirm students will be spaced in accordance with assessment specific spacing policy.</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={hasProperSpacing} name='hasProperSpacing' error={(getError('hasProperSpacing'))} onChange={(val) => { changed({ hasProperSpacing: val }); }} />
            </FieldRow>
            <FieldRow>
              <RadioLabel variant="body1">Please confirm students will be seated randomly (not alphabetically or in another predictable pattern)</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={hasRandomSeating} name='hasRandomSeating' error={(getError('hasRandomSeating'))} onChange={(val) => { changed({ hasRandomSeating: val }); }} />
            </FieldRow>
          </Grid>
        </Grid>
      </Panel>

      <Panel>
        <Title>Exam Administration Staff and Training</Title>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Individuals responsible for providing test administration training</FieldLabel>
              {hasError('testAdministrationTrainingIndividuals') &&
                <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>{getError('testAdministrationTrainingIndividuals')}</span>
              }
            </FieldRow>
            <FieldRow>
              {testTrainers.map((item, i) => {
                const isEditing = (trainerForm.data === item);
                return <NameTitleDetails key={`trainer-${i}`} item={item} readOnly={readOnly} isEditing={isEditing}
                  onRemove={(item) => { trainerEntities.remove(item); changed({ testAdministrationTrainingIndividuals: trainerEntities.items }); }}
                  onEdit={(item) => { trainerForm.open(item); }}
                />;
              })}
            </FieldRow>
            <FieldRow>
              {trainerForm.isOpen ?
                <NameTitleForm item={trainerForm.data} onCancel={() => { trainerForm.close(); }}
                  onComplete={(data) => {
                    const pos = trainerForm.data ? trainerEntities.match(trainerForm.data) : undefined;
                    trainerEntities.add(data, pos);
                    trainerForm.close();
                    changed({ testAdministrationTrainingIndividuals: trainerEntities.items });
                  }} /> :
                <>
                  {!readOnly && <IconButton onClick={() => { trainerForm.open(); }}><AddIcon />&nbsp;Add Person</IconButton>}
                </>
              }
            </FieldRow>

            <Divider />

            <FieldRow>
              <FieldLabel variant="body1">Where and when will training be provided?</FieldLabel>
              <TextArea readOnly={readOnly} name='trainingProvisionDetails' validation={getError('trainingProvisionDetails') ? 'error' : undefined} validationMsg={getError('trainingProvisionDetails')} onChange={(e) => { changed({ trainingProvisionDetails: e.target.value }); }} value={trainingProvisionDetails} />
            </FieldRow>

            <FieldRow>
              <FieldLabel variant="body1">Regarding your test day staffing plan; How many testing room staff members will be scheduled?</FieldLabel>
              <IntegerInput readOnly={readOnly} name='testingRoomStaffMembersCount' error={getError('testingRoomStaffMembersCount')} onChange={(val) => { changed({ testingRoomStaffMembersCount: val }); }} value={testingRoomStaffMembersCount} />
            </FieldRow>

            <FieldRow>
              <FieldLabel variant="body1">How many non-testing room staff members will be scheduled?</FieldLabel>
              <IntegerInput readOnly={readOnly} name='nonTestingRoomStaffMembersCount' error={getError('nonTestingRoomStaffMembersCount')} onChange={(val) => { changed({ nonTestingRoomStaffMembersCount: val }); }} value={nonTestingRoomStaffMembersCount} />
            </FieldRow>
          </Grid>
          <Grid item xs={6}>
            <FieldRow>
              <RadioLabel variant="body1">Confirm training will be in accordance with the assessment specific coordinator manual (IE: SAT Coordinator manual for SAT exam administration)</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={assessmentTrainingConfirmation} name='assessmentTrainingConfirmation' error={(getError('assessmentTrainingConfirmation'))} onChange={(val) => { changed({ assessmentTrainingConfirmation: val }); }} />
            </FieldRow>

            <FieldRow>
              <RadioLabel variant="body1">Confirm sufficient and continued communication will be sent to alert all staff and parents of test day, and to ensure no distractions from other students, announcements, bells, fire drills, etc.</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={testAlertConfirmation} name='testAlertConfirmation' error={(getError('testAlertConfirmation'))} onChange={(val) => { changed({ testAlertConfirmation: val }); }} />
            </FieldRow>

            <FieldRow>
              <FieldLabel variant="body1">Please provide your planned test day break schedule.</FieldLabel>
              <TextArea disabled={readOnly} value={plannedTestDayBreakSchedule}
                validation={hasError('plannedTestDayBreakSchedule') ? 'error' : undefined}
                validationMsg={getError('plannedTestDayBreakSchedule')}
                onChange={(e: any) => { changed({ plannedTestDayBreakSchedule: e.target.value }); }} />
            </FieldRow>
          </Grid>
        </Grid>
      </Panel>

      <Panel>
        <Title>Exam Storage Security</Title>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Name and title of individual responsible for receiving and storing secure materials</FieldLabel>
            </FieldRow>
            <FieldRow>
              <NameTitleFields readOnly={readOnly} item={secureMaterialsManagerIndividualDetails}
                errors={getFieldError('secureMaterialsManagerIndividualDetails')}
                onUpdate={(item) => { changed({ secureMaterialsManagerIndividualDetails: item }); }}
              />
            </FieldRow>

            <FieldRow>
              <FieldLabel variant="body1">Number of keys to secure material storage area (if applicable)</FieldLabel>
              <IntegerInput readOnly={readOnly} name='secureMaterialsStorageKeysCount' error={getError('secureMaterialsStorageKeysCount')} onChange={(val) => { changed({ secureMaterialsStorageKeysCount: val }); }} value={secureMaterialsStorageKeysCount} />
            </FieldRow>

            <FieldRow>
              <FieldLabel variant="body1">Where and how will materials be stored?</FieldLabel>
              <TextArea
                disabled={readOnly}
                validation={hasError('howToStoreMaterials') ? 'error' : undefined}
                validationMsg={getError('howToStoreMaterials')}
                onChange={(e: any) => { changed({ howToStoreMaterials: e.target.value }); }} value={howToStoreMaterials} />
            </FieldRow>
          </Grid>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Upload and describe pictures of the storage area</FieldLabel>
              {hasError('storageAreaPictures') &&
                <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>{getError('storageAreaPictures')}</span>
              }
            </FieldRow>
            <FieldRow>
              {storageAreaPictures.map((item, i) => {
                return <FieldRow key={`storage-area-pictures-${i}`}><FileDetails readOnly={readOnly} data={item} onRemove={(item) => {
                  storagePictureEntities.remove(item);
                  changed({ storageAreaPictures: storagePictureEntities.items });
                }} /></FieldRow>;
              })}
            </FieldRow>
            {!readOnly &&
              <>
                <FieldRow>
                  <FileUploader
                    uploadButton='Add Picture'
                    onUploadFile={async (file: File, description: string | undefined) => {
                      const fieldMaterial: IMaterial = await uploadFile(userName, { categoryId: categoryId }, file, description, 'storageAreaPictures', true);
                      storagePictureEntities.add(fieldMaterial);
                      changed({ storageAreaPictures: storagePictureEntities.items });
                    }}
                  />
                </FieldRow>
              </>
            }

            <Divider />

            <FieldRow>
              <FieldLabel variant="body1">Names and titles of individuals with access to secure storage</FieldLabel>
              {hasError('secureMaterialsAccessIndividualDetails') &&
                <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>{getError('secureMaterialsAccessIndividualDetails')}</span>
              }
            </FieldRow>
            <FieldRow>
              {secureMaterialsAccessIndividualDetails.map((item, i) => {
                const isEditing = (accessForm.data === item);
                return <NameTitleDetails key={`access-${i}`} item={item} readOnly={readOnly} isEditing={isEditing}
                  onRemove={(item) => { accessEntities.remove(item); changed({ secureMaterialsAccessIndividualDetails: accessEntities.items }); }}
                  onEdit={(item) => { accessForm.open(item); }}
                />;
              })}
            </FieldRow>
            <FieldRow>
              {accessForm.isOpen ?
                <NameTitleForm item={accessForm.data}
                  onCancel={() => { accessForm.close(); }}
                  onComplete={(data) => {
                    const pos = accessForm.data ? accessEntities.match(accessForm.data) : undefined;
                    accessEntities.add(data, pos);
                    accessForm.close();
                    changed({ secureMaterialsAccessIndividualDetails: accessEntities.items });
                  }} /> :
                <>
                  {!readOnly && <IconButton onClick={() => { accessForm.open(); }}><AddIcon />&nbsp;Add Person</IconButton>}
                </>
              }
            </FieldRow>
          </Grid>
        </Grid>
      </Panel>


      <Panel>
        <Title>Exam Administration Material Distribution</Title>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Name and title of test supervisor?</FieldLabel>
            </FieldRow>
            <FieldRow>
              <NameTitleFields readOnly={readOnly} item={testSupervisorDetail}
                errors={getFieldError('testSupervisorDetail')}
                onUpdate={(item) => { changed({ testSupervisorDetail: item }); }}
              />
            </FieldRow>

            <FieldRow>
              <FieldLabel variant="body1">Describe how testing materials will be prepared for separate testing rooms.</FieldLabel>
              <TextArea disabled={readOnly} value={testingMaterialsPreparationDescription}
                validation={hasError('testingMaterialsPreparationDescription') ? 'error' : undefined}
                validationMsg={getError('testingMaterialsPreparationDescription')}
                onChange={(e: any) => { changed({ testingMaterialsPreparationDescription: e.target.value }); }} />
            </FieldRow>

            <FieldRow>
              <RadioLabel variant="body1">Confirm test center supervisor will ensure proper handling, accounting and distribution of materials and adherence to test day procedures.</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={testSupervisorConfirmation} name='testSupervisorConfirmation' error={(getError('testSupervisorConfirmation'))} onChange={(val) => { changed({ testSupervisorConfirmation: val }); }} />
            </FieldRow>
          </Grid>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Names and titles of individual responsible for exam material distribution to testing rooms</FieldLabel>
              {hasError('testingRoomExamMaterialDistributionIndividuals') &&
                <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>{getError('testingRoomExamMaterialDistributionIndividuals')}</span>
              }
            </FieldRow>
            <FieldRow>
              {testingRoomExamMaterialDistributionIndividuals.map((item, i) => {
                const isEditing = (distributionForm.data === item);
                return <NameTitleDetails key={`distribution-${i}`} item={item} readOnly={readOnly} isEditing={isEditing}
                  onRemove={(item) => { distributionEntities.remove(item); changed({ testingRoomExamMaterialDistributionIndividuals: distributionEntities.items }); }}
                  onEdit={(item) => { distributionForm.open(item); }}
                />;
              })}
            </FieldRow>
            <FieldRow>
              {distributionForm.isOpen ?
                <NameTitleForm item={distributionForm.data}
                  onCancel={() => { distributionForm.close(); }}
                  onComplete={(data) => {
                    const pos = distributionForm.data ? distributionEntities.match(distributionForm.data) : undefined;
                    distributionEntities.add(data, pos);
                    distributionForm.close();
                    changed({ testingRoomExamMaterialDistributionIndividuals: distributionEntities.items });
                  }} /> :
                <>
                  {!readOnly && <IconButton onClick={() => { distributionForm.open(); }}><AddIcon />&nbsp;Add Person</IconButton>}
                </>
              }
            </FieldRow>

            <Divider />

            <FieldRow>
              <FieldLabel variant="body1">Distance between the storage location and the testing rooms</FieldLabel>
            </FieldRow>
            <DistanceFields item={distanceBetweenTestingRoomAndStorageArea} readOnly={readOnly} showDiff={false}
              errors={getFieldError('distanceBetweenTestingRoomAndStorageArea')}
              onUpdate={(distance) => { changed({ distanceBetweenTestingRoomAndStorageArea: distance }); }}
            />

            <FieldRow>
              <FieldLabel variant="body1">How will exams be transported to testing rooms?</FieldLabel>
              <TextArea disabled={readOnly} value={testingRoomsTransportation}
                validation={hasError('testingRoomsTransportation') ? 'error' : undefined}
                validationMsg={getError('testingRoomsTransportation')}
                onChange={(e: any) => { changed({ testingRoomsTransportation: e.target.value }); }} />
            </FieldRow>
          </Grid>
        </Grid>
      </Panel>

      <Panel>
        <Title>Exam Administration Security</Title>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <FieldRow>
              <RadioLabel variant="body1">Confirm outside students will be verified with acceptable ID.</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={outsideStudentsVerification} name='outsideStudentsVerification' error={(getError('outsideStudentsVerification'))}
                onChange={(val) => {
                  changed({
                    outsideStudentsVerification: val,
                    listAcceptedIDForms: val ? listAcceptedIDForms : undefined
                  });
                }} />
            </FieldRow>

            {outsideStudentsVerification &&
              <FieldRow>
                <FieldLabel variant="body1">If yes: Please list accepted forms of ID.</FieldLabel>
                <TextArea disabled={readOnly} value={listAcceptedIDForms}
                  validation={hasError('listAcceptedIDForms') ? 'error' : undefined}
                  validationMsg={getError('listAcceptedIDForms')}
                  onChange={(e: any) => { changed({ listAcceptedIDForms: e.target.value }); }} />
              </FieldRow>
            }

            <FieldRow>
              <RadioLabel variant="body1">Confirm your organization will adhere to outlined policies regarding testing and security violations.</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={orgTestPolicyConfirmation} name='orgTestPolicyConfirmation' error={(getError('orgTestPolicyConfirmation'))}
                onChange={(val) => { changed({ orgTestPolicyConfirmation: val }); }} />
            </FieldRow>
          </Grid>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Explain how will late arrivals be handled?</FieldLabel>
              <TextArea disabled={readOnly} value={lateArrivalsHandleDetails}
                validation={hasError('lateArrivalsHandleDetails') ? 'error' : undefined}
                validationMsg={getError('lateArrivalsHandleDetails')}
                onChange={(e: any) => { changed({ lateArrivalsHandleDetails: e.target.value }); }} />
            </FieldRow>

            <FieldRow>
              <FieldLabel variant="body1">How will your facility handle test takers over 21 years of age?</FieldLabel>
              <TextArea disabled={readOnly} value={facilityHandleTestTakersDetails}
                validation={hasError('facilityHandleTestTakersDetails') ? 'error' : undefined}
                validationMsg={getError('facilityHandleTestTakersDetails')}
                onChange={(e: any) => { changed({ facilityHandleTestTakersDetails: e.target.value }); }} />
            </FieldRow>
          </Grid>
        </Grid>
      </Panel>

      <Panel>
        <Title>College Board Approved Accommodations</Title>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Name & Title of coordinator for testing with accommodations</FieldLabel>
            </FieldRow>
            <FieldRow>
              <NameTitleFields readOnly={readOnly} item={accomodationTestingCoordinatorDetails}
                errors={getFieldError('accomodationTestingCoordinatorDetails')}
                onUpdate={(item) => { changed({ accomodationTestingCoordinatorDetails: item }); }}
              />
            </FieldRow>
          </Grid>
          <Grid item xs={6}>
            <FieldRow>
              <RadioLabel variant="body1">Will your facility have separate testing rooms available for students with accommodations?</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={accomodationSeparateTestingRoomsConfirmation} name='accomodationSeparateTestingRoomsConfirmation' error={(getError('accomodationSeparateTestingRoomsConfirmation'))}
                onChange={(val) => { changed({ accomodationSeparateTestingRoomsConfirmation: val }); }} />
            </FieldRow>

            <FieldRow>
              <RadioLabel variant="body1">Will your facility remain available for extended time? (IE: testing over two days)</RadioLabel>
              <BooleanRadio readOnly={readOnly} value={extendedTimeFacilityRemainConfirmation} name='extendedTimeFacilityRemainConfirmation' error={(getError('extendedTimeFacilityRemainConfirmation'))}
                onChange={(val) => { changed({ extendedTimeFacilityRemainConfirmation: val }); }} />
            </FieldRow>
          </Grid>
        </Grid>
      </Panel>

      <Panel>
        <Title>Post Administration Tasks</Title>
        <Grid container spacing={6}>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Name & title of individual responsible for accounting for, inventorying and returning test materials </FieldLabel>
            </FieldRow>
            <FieldRow>
              <NameTitleFields readOnly={readOnly} item={testingMaterialsAccountingIndividualDetails}
                errors={getFieldError('testingMaterialsAccountingIndividualDetails')}
                onUpdate={(item) => { changed({ testingMaterialsAccountingIndividualDetails: item }); }}
              />
            </FieldRow>
          </Grid>
          <Grid item xs={6}>
            <FieldRow>
              <FieldLabel variant="body1">Where will secure materials be secured until returned?</FieldLabel>
              <Input disabled={readOnly} validation={hasError('secureMaterialsReturnLocation') ? 'error' : undefined} validationMsg={getError('secureMaterialsReturnLocation')}
                onChange={(e) => { changed({ secureMaterialsReturnLocation: e.target.value }); }}
                value={secureMaterialsReturnLocation}
              />
            </FieldRow>
          </Grid>
        </Grid>
      </Panel>
    </>;
  };

  return (
    <div>
      {render()}
    </div>
  );
};